import { Link } from "react-router-dom";

/**
 * A sample's storage path, as a way to get there.
 *
 * The path was printed as flat text, so knowing a specimen was in
 * "Demo freezer -80 / Rack 1 / Box 1 / B4" still meant navigating to Storage,
 * picking the freezer, the rack, the box, and then finding B4 by eye. The API
 * has carried the parts as ids since UX-07 (`enrich_samples` in
 * `backend/server.py` builds `location.freezer/rack/box/position`); this spends
 * them on links.
 *
 * The slashes are not decoration — the path really is a tree, so every segment
 * is navigable the way a file path is. A freezer or rack opens the storage map
 * focused on it; the box opens its own page; the position opens that page with
 * the well selected. Only the last two have pages of their own, so the first
 * two focus the map through the query string rather than pretending otherwise.
 *
 * Works for a test row as well as a sample row: `enrich_tests` copies the same
 * `location` onto worklist items, which is where an analyst reads it.
 */
const SEGMENT_CLS = "text-teal-800 underline-offset-2 hover:underline";

export default function SampleLocation({ sample, className = "" }) {
  const label = sample?.location_label;
  const location = sample?.location;

  if (!label) return <span className="text-slate-400">—</span>;

  // A label with no structure behind it is a sample whose path cannot be
  // addressed. Rendering dead links would be worse than plain text.
  if (!location?.box?.id) return <span className={className}>{label}</span>;

  const { freezer, rack, box, position } = location;
  const code = sample.sample_code || box.id;

  const segments = [];
  if (freezer?.id) {
    segments.push({
      key: "freezer",
      name: freezer.name,
      to: `/storage?freezer=${encodeURIComponent(freezer.id)}`,
      title: `Show ${freezer.name} on the storage map`,
    });
  }
  if (rack?.id) {
    segments.push({
      key: "rack",
      name: rack.name,
      to: `/storage?rack=${encodeURIComponent(rack.id)}`,
      title: `Show ${rack.name} on the storage map`,
    });
  }
  segments.push({
    key: "box",
    name: box.name,
    to: `/storage/boxes/${box.id}`,
    title: `Open ${box.name}`,
  });
  if (position) {
    segments.push({
      key: "position",
      name: position,
      to: `/storage/boxes/${box.id}?position=${encodeURIComponent(position)}`,
      title: `Show position ${position} in ${box.name}`,
    });
  }

  return (
    <span className={className} data-testid={`location-path-${code}`}>
      {segments.map((segment, index) => (
        <span key={segment.key}>
          {index > 0 && <span className="text-slate-300"> / </span>}
          <Link
            to={segment.to}
            title={segment.title}
            data-testid={`location-${segment.key}-${code}`}
            className={SEGMENT_CLS}
          >
            {segment.name}
          </Link>
        </span>
      ))}
    </span>
  );
}
